import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { toast } from "sonner"

import type { HostedAgentArtifact } from "@/api/client/hosted-agents-artifacts"

import { Copy, FileText, GitBranch, Loader2, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { ArtifactDocument } from "./artifact-document"
import {
  useDeleteHostedAgentArtifact,
  useHostedAgentArtifact,
} from "../hooks/use-hosted-agent-artifacts"
import {
  ARTIFACT_KIND_LABEL_KEYS,
  artifactDate,
  artifactDeletedToast,
  libraryArtifactDisplayContent,
} from "../utils/agent-artifacts-utils"

type ArtifactViewerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  agentSlug: string
  /** The artifact the user clicked (latest version of its family). */
  artifact: HostedAgentArtifact | null
  /** "Reuse": insert the artifact's content into the composer as context. */
  onReuse: (artifact: HostedAgentArtifact) => void
  /** "Continue working": start a new thread seeded with the artifact. */
  onContinue: (artifact: HostedAgentArtifact) => void
}

type ArtifactBodyProps = {
  agentSlug: string
  artifact: HostedAgentArtifact
}

function ArtifactBody({ artifact, agentSlug }: ArtifactBodyProps) {
  const { t } = useTranslation()

  if (artifact.kind === "document")
    return <ArtifactDocument agentSlug={agentSlug} artifact={artifact} />

  if (artifact.content == null) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        <FileText aria-hidden="true" className="size-4" />
        {t("agents.artifacts.document_unavailable")}
      </p>
    )
  }

  // Early publishes stored prose + raw `<artifact>` XML: show the block's content.
  const content = libraryArtifactDisplayContent(artifact.content)

  if (artifact.kind === "text") {
    return <pre className="font-sans text-sm break-words whitespace-pre-wrap">{content}</pre>
  }

  // No `rehype-raw`: artifact content is user-published Markdown and must
  // never render embedded HTML.
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  )
}

/**
 * Artifact view (B4): rendered content, version history, and the three
 * actions that make the library worth having — reuse, continue working
 * (→ next version) and delete (author only).
 */
export function ArtifactViewerDialog({
  open,
  onOpenChange,
  agentSlug,
  artifact,
  onReuse,
  onContinue,
}: ArtifactViewerDialogProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"
  const currentUserOid = SettingsService.getInstance().claims.oid

  // Which version of the family is displayed; defaults to the clicked one.
  const [selectedId, setSelectedId] = useState<string | undefined>(artifact?.id)
  // The version the family is fetched through. Starts as the clicked one and
  // moves to a surviving sibling when that version is deleted (a 404 on the
  // deleted id would otherwise resurrect it from the `artifact` prop).
  const [anchorId, setAnchorId] = useState<string | undefined>(artifact?.id)
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    if (open) {
      setSelectedId(artifact?.id)
      setAnchorId(artifact?.id)
      setDeletedIds(new Set())
    }
  }, [open, artifact?.id])

  const familyQuery = useHostedAgentArtifact(agentSlug, open ? anchorId : undefined)
  const versions = useMemo(() => {
    const list = familyQuery.data?.versions ?? (artifact ? [artifact] : [])
    return list
      .filter((version) => !deletedIds.has(version.id))
      .sort((a, b) => b.version - a.version)
  }, [familyQuery.data?.versions, artifact, deletedIds])

  const fetched = familyQuery.data
  const current: HostedAgentArtifact | undefined =
    versions.find((version) => version.id === selectedId) ??
    (fetched && fetched.artifact.id === selectedId ? fetched.artifact : undefined) ??
    versions.at(0) ??
    (artifact && !deletedIds.has(artifact.id) ? artifact : undefined)

  const isAuthor = !!current && !!currentUserOid && current.created_by === currentUserOid

  const deleteArtifact = useDeleteHostedAgentArtifact(agentSlug)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const handleDelete = async () => {
    if (!current) return
    try {
      await deleteArtifact.mutateAsync(current.id)
      const remaining = versions.filter((version) => version.id !== current.id)
      // Name the version while siblings survive (#3207).
      const deletedToast = artifactDeletedToast(current, remaining.length)
      toast.success(t(deletedToast.key, deletedToast.options))
      setConfirmDelete(false)
      if (remaining.length === 0) {
        onOpenChange(false)
      } else {
        setDeletedIds((previous) => new Set(previous).add(current.id))
        setSelectedId(remaining[0].id)
        // Re-anchor the family fetch on a surviving version (the deleted id 404s).
        setAnchorId(remaining[0].id)
      }
    } catch (error) {
      toast.error(t("agents.artifacts.delete_failed"), {
        description: error instanceof Error ? error.message : undefined,
      })
    }
  }

  const handleCopy = async () => {
    if (!current?.content) return
    try {
      await navigator.clipboard.writeText(libraryArtifactDisplayContent(current.content))
      toast.success(t("agents.artifacts.copied"))
    } catch {
      toast.error(t("agents.artifacts.copy_failed"))
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
              <span className="min-w-0 break-words">{current?.title ?? ""}</span>
              {current ? (
                <>
                  <Badge variant="secondary">{t(ARTIFACT_KIND_LABEL_KEYS[current.kind])}</Badge>
                  <Badge variant="outline">
                    {t("agents.artifacts.version", { version: current.version })}
                  </Badge>
                </>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              {current
                ? t("agents.artifacts.meta", {
                    name: current.created_by_name || t("agents.artifacts.unknown_author"),
                    date: artifactDate(current.created_at, locale),
                  })
                : ""}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="flex min-h-0 flex-col gap-4 sm:flex-row">
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto rounded-lg border p-4">
              {current?.summary ? (
                <p className="text-muted-foreground mb-3 text-sm italic">{current.summary}</p>
              ) : null}
              {current ? <ArtifactBody agentSlug={agentSlug} artifact={current} /> : null}
            </div>

            <aside className="flex w-full shrink-0 flex-col gap-1 sm:w-48">
              <p className="text-muted-foreground px-2 text-[11px] font-semibold tracking-wider uppercase">
                {t("agents.artifacts.history")}
              </p>
              {familyQuery.isPending && versions.length <= 1 ? (
                <div className="flex items-center gap-2 px-2 py-1 text-xs">
                  <Loader2 aria-hidden="true" className="size-3 animate-spin" />
                  <span className="text-muted-foreground">{t("agents.artifacts.loading")}</span>
                </div>
              ) : null}
              <ul className="flex max-h-48 flex-col gap-0.5 overflow-y-auto sm:max-h-none">
                {versions.map((version) => {
                  const isActive = version.id === current?.id
                  return (
                    <li key={version.id}>
                      <button
                        aria-current={isActive ? "true" : undefined}
                        type="button"
                        className={cn(
                          "focus-visible:ring-ring flex w-full flex-col rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none",
                          isActive
                            ? "border-foreground bg-primary text-primary-foreground border font-semibold"
                            : "hover:bg-accent hover:text-accent-foreground"
                        )}
                        onClick={() => setSelectedId(version.id)}
                      >
                        <span>{t("agents.artifacts.version", { version: version.version })}</span>
                        <span className="text-muted-foreground text-[11px] font-normal">
                          {artifactDate(version.created_at, locale)}
                          {version.created_by_name ? ` · ${version.created_by_name}` : ""}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </aside>
          </DialogBody>

          <DialogFooter className="flex-wrap gap-2 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {isAuthor ? (
                <Button
                  disabled={deleteArtifact.isPending}
                  variant="outline"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 aria-hidden="true" className="text-foreground size-4" />
                  {t("agents.artifacts.delete")}
                </Button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!current?.content}
                variant="outline"
                onClick={() => void handleCopy()}
              >
                <Copy aria-hidden="true" className="size-4" />
                {t("agents.artifacts.copy")}
              </Button>
              <Button
                disabled={!current?.content}
                variant="secondary"
                onClick={() => current && onReuse(current)}
              >
                {t("agents.artifacts.reuse")}
              </Button>
              <Button disabled={!current?.content} onClick={() => current && onContinue(current)}>
                <GitBranch aria-hidden="true" className="size-4" />
                {t("agents.artifacts.continue")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("agents.artifacts.delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("agents.artifacts.delete_confirm_description", {
                title: current?.title ?? "",
                version: current?.version ?? 1,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteArtifact.isPending}>
              {t("agents.artifacts.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteArtifact.isPending}
              onClick={(event) => {
                event.preventDefault()
                void handleDelete()
              }}
            >
              {deleteArtifact.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              {t("agents.artifacts.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
